// The area's building, and moving it to another one (docs/mockups/buildings-ipad.html §4):
// the area keeps its surfaces, photos and ticks; at the desk it is then priced
// × the new building's count, so a move that changes the count asks first.
import { useRef, useState } from 'react';
import { ConfirmDialog, Label } from '@higgins/ui';
import { buildingLabel, measureBuildings, moveWarning } from '../lib/buildings';
import type { DeviceRecord } from '../lib/device-db';
import { moveArea } from '../lib/jobs';

interface Props {
  area: DeviceRecord;
  records: readonly DeviceRecord[];
}

export function AreaBuilding({ area, records }: Props) {
  const [asking, setAsking] = useState<{ to: DeviceRecord; warning: string } | null>(null);
  const select = useRef<HTMLSelectElement>(null);
  const from = records.find((r) => r.table === 'building' && r.id === area.parentId);
  const buildings = from ? measureBuildings(records, from.parentId) : [];
  // One building on the job: nothing to move to, so the picker isn't shown.
  if (!from || buildings.length < 2) return null;
  const areaName = String(area.fields.hig_name ?? 'This area');

  function choose(id: string) {
    const to = buildings.find((b) => b.id === id);
    if (!to || to.id === from!.id) return;
    const warning = moveWarning(areaName, from!, to);
    if (warning) setAsking({ to, warning });
    else void moveArea(area, to.id);
  }

  return (
    <div>
      <Label htmlFor="area-building">Building</Label>
      <select
        id="area-building"
        ref={select}
        value={from.id}
        onChange={(e) => choose(e.target.value)}
        className="mt-1 block h-11 w-full rounded-md border border-slate-300 bg-white px-2 text-base"
      >
        {buildings.map((b) => (
          <option key={b.id} value={b.id}>
            {buildingLabel(b, records)}
          </option>
        ))}
      </select>
      <p className="mt-1 text-xs text-slate-500">Change it to move this area, with its surfaces and photos.</p>
      <ConfirmDialog
        open={!!asking}
        onOpenChange={(open) => !open && setAsking(null)}
        title={`Move ${areaName}?`}
        description={asking?.warning}
        confirmLabel="Move"
        onConfirm={() => {
          if (asking) void moveArea(area, asking.to.id);
          setAsking(null);
        }}
        returnFocus={select}
      />
    </div>
  );
}
